import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, FreeMode, Mousewheel, Scrollbar } from "swiper/modules";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { getAllPhotoUrls, getSingleImageUrl } from "../utils/Helpers";
import { galerie_projects, sortGalerieProjects } from "../data/galerieProjects";
import { STATUS_META, OPEN_STATUS_META } from "../data/projectStatusMeta";
import "swiper/css";
import "swiper/css/free-mode";

export default function GalerieProjectsSlider({
  title = "What's Next For Galerie",
  subhead = "New Developments",
  slidesPerView = 3,
  class_name = "",
  projects: projectsOverride,
}) {
  const projects = sortGalerieProjects(projectsOverride || galerie_projects);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const scrollbarRef = useRef(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [photoUrls, setPhotoUrls] = useState({});
  // True until the Storage fetch below settles — gates whether a slide
  // shows the .galerie-project-img-skeleton loading block or its <img>
  // (real photo, or the placeholderImg fallback for a project whose photo
  // never turned up in Storage).
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getAllPhotoUrls("galerie_projects"),
      // This slider is also reused for the Management page's "Assets Under
      // Management" section (projects={management_assets}, imgIds like
      // "vpm_man_compressed") and Corso Tysons (imgId "Tysons_Recolor_2",
      // which actually lives here, not in galerie_projects) — without these
      // two, those cards would never find their real photo and would show
      // their placeholderImg forever instead of just while loading.
      getAllPhotoUrls("management"),
      getAllPhotoUrls("corso_page"),
      // Reuse the existing Corso "Our Locations" photos for these two
      // projects instead of matching by id — matching ids would collide
      // with the Locations section's own images on the Corso page itself.
      getSingleImageUrl("corso_page/corso-locations-2.webp"),
      getSingleImageUrl("corso_page/corso-locations-3.webp"),
    ]).then(([galerieProjects, management, corsoPage, druidHillsUrl, chevyChaseUrl]) => {
      setPhotoUrls({
        ...galerieProjects,
        ...management,
        ...corsoPage,
        ...(druidHillsUrl && { "galerie-project-druid-hills": druidHillsUrl }),
        ...(chevyChaseUrl && { "galerie-project-chevy-chase": chevyChaseUrl }),
      });
      setIsLoading(false);
    });
  }, []);

  const updateEdges = (swiper) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  return (
    <div className={`galerie-projects-section ${class_name}`}>
      <div className="galerie-projects-header">
        <div className="galerie-projects-header-text">
          <p className="health-header-title">{subhead}</p>
          <h2 className="galerie-projects-title">{title}</h2>
        </div>

        <div className="galerie-projects-arrows">
          <div
            ref={prevRef}
            className={`galerie-projects-prev-arrow ${
              isBeginning ? "disabled" : ""
            }`}
            data-testid="galerie-projects-prev-btn"
          >
            <ArrowLeft
              className={
                isBeginning
                  ? "galerie-projects-arrow-icon-disabled"
                  : "galerie-projects-arrow-icon"
              }
            />
          </div>

          <div
            ref={nextRef}
            className={`galerie-projects-next-arrow ${
              isEnd ? "disabled" : ""
            }`}
            data-testid="galerie-projects-next-btn"
          >
            <ArrowRight
              className={
                isEnd
                  ? "galerie-projects-arrow-icon-disabled"
                  : "galerie-projects-arrow-icon"
              }
            />
          </div>
        </div>
      </div>

      <div
        ref={scrollbarRef}
        className="slider-progress-track"
        data-testid="galerie-projects-progress"
      />

      <Swiper
        modules={[Navigation, FreeMode, Mousewheel, Scrollbar]}
        spaceBetween={24}
        slidesPerView={1}
        slidesPerGroup={1}
        freeMode={{
          enabled: true,
          sticky: false,
          momentum: true,
        }}
        mousewheel={{
          forceToAxis: true,
        }}
        grabCursor={true}
        breakpoints={{
          760: { slidesPerView: Math.min(2, slidesPerView) },
          1100: { slidesPerView },
        }}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current,
        }}
        // Scrollbar's drag-thumb size/position is computed from the real
        // visible-vs-total slide ratio and recalculates on its own on
        // resize/breakpoint changes — see .slider-progress-track in
        // _components.scss for the red-highlight styling. draggable stays
        // false: it's a read-only indicator, and turning it on wires up
        // document-level pointermove/pointerup listeners (Swiper's
        // onDragMove/onDragEnd) that can throw once the React wrapper's own
        // prop-diffing re-inits the scrollbar module across re-renders.
        scrollbar={{
          el: scrollbarRef.current,
          draggable: false,
          hide: false,
        }}
        onBeforeInit={(swiper) => {
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
          swiper.params.scrollbar.el = scrollbarRef.current;
        }}
        onSwiper={updateEdges}
        onSlideChange={updateEdges}
        className="galerie-projects-swiper"
      >
        {projects.map((p) => {
          const status = STATUS_META[p.status] || OPEN_STATUS_META;
          return (
          <SwiperSlide key={p.id} className="galerie-project-slide">
            <a
              href={p.link}
              target="_blank"
              rel="noreferrer"
              className="galerie-project-card"
              data-testid={`galerie-project-${p.id}`}
              onMouseOver={() => setHoveredId(p.id)}
              onMouseOut={() => setHoveredId(null)}
            >
              <div className="galerie-project-img-outer">
                <span className={`galerie-project-badge ${status.className}`}>
                  {status.label}
                </span>
                <div className="galerie-project-img-wrap">
                  {isLoading ? (
                    <div
                      className="galerie-project-img-skeleton"
                      data-testid={`galerie-project-skeleton-${p.id}`}
                    />
                  ) : (
                    <img
                      loading="lazy"
                      src={photoUrls[p.imgId] || p.placeholderImg}
                      className="galerie-project-img"
                      alt={`Rendering or photo of ${p.name}`}
                    />
                  )}
                </div>
              </div>
              <div className="galerie-project-body">
                <div
                  className="galerie-project-name-row"
                  style={
                    hoveredId === p.id
                      ? { color: "#A12A2B" }
                      : { color: "black" }
                  }
                >
                  <h3 className="galerie-project-name">{p.name}</h3>
                  <ArrowUpRight size={20} />
                </div>
                <p className="galerie-project-location">{p.location}</p>
                <p className="galerie-project-blurb">{p.blurb}</p>
              </div>
            </a>
          </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
}
